"use client";

import { CSSProperties, ReactNode } from "react";
import { Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { FlipCard, FlipCardsAppearance } from "@/lib/utils/lomdaV3/types";
import { FLIP_CARDS_CONFIG } from "../../../../lib/blockRegistry";
import { hexToHslAlpha } from "../../../../lib/theme";
import { TabIcon } from "../../../../lib/tabIcons";
import { ICON_SIZE } from "../../../../lib/iconSizes";
import { IconPicker } from "../../shared/iconPicker";

/** Grid: one column on mobile, the configured column count from md up. */
export const gridStyle = (columns: number, gap: number): CSSProperties =>
    ({
        "--flip-cols": `repeat(${columns}, minmax(0, 1fr))`,
        gap,
    }) as CSSProperties;
export const gridClass =
    "grid grid-cols-1 md:[grid-template-columns:var(--flip-cols)]";

/** Static cards follow the block's own width: up to `columns` per row, and a
 *  card drops to the next row before it gets narrower than the minimum. */
export const staticGridStyle = (columns: number, gap: number): CSSProperties => ({
    gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, max(${FLIP_CARDS_CONFIG.staticMinCardWidth}px, (100% - ${(columns - 1) * gap}px) / ${columns})), 1fr))`,
    gap,
});

/**
 * A card's own background wins over the block's. An uploaded image shows at
 * full strength on every face; the optional scrim darkens it for readability —
 * the same overlay control the lomda background uses.
 */
export function cardFaceStyle(
    card: FlipCard,
    appearance: FlipCardsAppearance
): CSSProperties {
    const image = card.appearance?.backgroundImage;
    const color = card.appearance?.cardBackground ?? appearance.cardBackground;
    const textColor = card.appearance?.textColor;
    // The faces' text uses `text-foreground` and the muted token, so recolour
    // via those vars (plus `color` for inheriting text); the muted shade keeps
    // its hierarchy as the chosen colour at 72% (a non-hex colour can't become
    // an hsl() token and stays at full strength).
    const textVars = textColor
        ? ({
              color: textColor,
              "--foreground": textColor,
              "--lomda-muted-text":
                  hexToHslAlpha(textColor, 0.72) ??
                  `color-mix(in srgb, ${textColor} 72%, transparent)`,
          } as CSSProperties)
        : {};
    if (!image) return { backgroundColor: color, ...textVars };
    const scrim = card.appearance?.scrim ?? 0;
    const url = `url("${image.replace(/"/g, "%22")}")`;
    return {
        backgroundColor: color,
        backgroundImage: scrim > 0
            ? `linear-gradient(rgba(0,0,0,${scrim}), rgba(0,0,0,${scrim})), ${url}`
            : url,
        backgroundSize: "cover",
        backgroundPosition: "center",
        ...textVars,
    };
}

/**
 * A face sits on the card colour, so its text follows "Text on cards" — unless
 * the author picked a per-card colour, which `cardFaceStyle` applies inline.
 */
export const faceTextClass = (card: FlipCard) =>
    card.appearance?.textColor ? undefined : "lomda-v3-card-text";

export const faceBorder = (appearance: FlipCardsAppearance): CSSProperties => ({
    borderColor: appearance.border,
    borderWidth: appearance.borderWidth,
    borderStyle: "solid",
});

export function CardFrame({
    card,
    appearance,
    focused = false,
    onFocus,
    className,
    style,
    children,
}: {
    card: FlipCard;
    appearance: FlipCardsAppearance;
    focused?: boolean;
    onFocus?: () => void;
    className: string;
    style?: CSSProperties;
    children: ReactNode;
}) {
    return (
        <div
            onPointerDown={onFocus}
            className={cn(
                className,
                "rounded-[20px] p-5 transition",
                faceTextClass(card),
                focused &&
                    "ring-2 ring-offset-2 ring-[hsl(var(--platform-primary))]"
            )}
            style={{
                ...cardFaceStyle(card, appearance),
                ...faceBorder(appearance),
                ...style,
            }}
        >
            {children}
        </div>
    );
}

export function CardIcon({
    icon,
    appearance,
}: {
    icon: string;
    appearance: FlipCardsAppearance;
}) {
    return (
        <span
            className="flex size-[50px] items-center justify-center rounded-lg"
            style={{
                backgroundColor: appearance.iconBackground,
                color: "hsl(var(--lomda-primary))",
            }}
        >
            <TabIcon name={icon} className={ICON_SIZE.flipCardIcon} />
        </span>
    );
}

export function CardIconPicker({
    icon,
    appearance,
    onPick,
}: {
    icon: string;
    appearance: FlipCardsAppearance;
    onPick: (icon: string) => void;
}) {
    return (
        <IconPicker value={icon} onPick={onPick}>
            <button
                type="button"
                aria-label={t("lomda-v3-editor.flip.select-icon")}
                className="flex size-[50px] shrink-0 items-center justify-center rounded-lg p-1 transition hover:opacity-90"
                style={
                    icon
                        ? {
                              backgroundColor: appearance.iconBackground,
                              color: "hsl(var(--lomda-primary))",
                          }
                        : {
                              backgroundColor: "hsl(var(--lomda-muted))",
                              border: "1px dashed hsl(var(--lomda-border-dashed))",
                              color: "hsl(var(--lomda-muted-text))",
                          }
                }
            >
                {icon ? (
                    <TabIcon name={icon} className={ICON_SIZE.flipCardIcon} />
                ) : (
                    <Plus className={ICON_SIZE.pickerPlus} />
                )}
            </button>
        </IconPicker>
    );
}
